import { supabase } from './supabaseClient'
import { cacheService } from './cacheService'

/**
 * Service API optimisé avec cache pour les données du portfolio
 */
export const portfolioAPI = {
  /**
   * Charger les données "À propos"
   */
  async getAPropos() {
    const cacheKey = 'apropos'
    const cached = cacheService.get(cacheKey)
    
    if (cached) {
      return { data: cached, error: null, fromCache: true }
    }

    try {
      const { data, error } = await supabase
        .from('apropos')
        .select('*')
        .limit(1)
        .single()

      if (error) {
        console.error('Erreur Supabase getAPropos:', error)
        return { data: null, error, fromCache: false }
      }

      if (data) {
        cacheService.set(cacheKey, data)
      }

      return { data, error: null, fromCache: false }
    } catch (error) {
      console.error('Exception dans getAPropos:', error)
      return { data: null, error, fromCache: false }
    }
  },

  /**
   * Charger les formations
   */
  async getFormations() {
    const cacheKey = 'formations'
    const cached = cacheService.get(cacheKey)
    
    if (cached) {
      return { data: cached, error: null, fromCache: true }
    }

    try {
      const { data, error } = await supabase
        .from('formation')
        .select('*')
        .order('annee', { ascending: false })

      if (error) {
        console.error('Erreur Supabase getFormations:', error)
        return { data: null, error, fromCache: false }
      }

      if (data) {
        cacheService.set(cacheKey, data)
      }

      return { data: data || [], error: null, fromCache: false }
    } catch (error) {
      console.error('Exception dans getFormations:', error)
      return { data: [], error, fromCache: false }
    }
  },

  /**
   * Charger les projets avec leurs compétences
   */
  async getProjets() {
    const cacheKey = 'projets'
    const cached = cacheService.get(cacheKey)
    
    if (cached) {
      return { data: cached, error: null, fromCache: true }
    }

    try {
      const { data, error } = await supabase
        .from('projet')
        .select(`
          *,
          competences:projet_competence(
            competence:competence(*)
          )
        `)

      if (error) {
        console.error('Erreur Supabase getProjets:', error)
        return { data: [], error, fromCache: false }
      }

      // Traiter les données pour aplatir la structure
      const processedData = (data || []).map(projet => ({
        ...projet,
        competences: projet.competences 
          ? projet.competences
              .map(pc => pc?.competence)
              .filter(Boolean) 
          : []
      }))

      if (processedData.length > 0) {
        cacheService.set(cacheKey, processedData)
      }

      return { data: processedData, error: null, fromCache: false }
    } catch (error) {
      console.error('Exception dans getProjets:', error)
      return { data: [], error, fromCache: false }
    }
  },

  /**
   * Charger les compétences avec formations et projets
   */
  async getCompetences() {
    const cacheKey = 'competences'
    const cached = cacheService.get(cacheKey)
    
    if (cached) {
      return { data: cached, error: null, fromCache: true }
    }

    try {
      const { data, error } = await supabase
        .from('competence')
        .select(`
          *,
          formation:formation(*),
          projets:projet_competence(
            projet:projet(*)
          )
        `)

      if (error) {
        console.error('Erreur Supabase getCompetences:', error)
        return { data: [], error, fromCache: false }
      }

      // Reformater les données pour aplatir la structure
      const processedData = (data || []).map(competence => ({
        ...competence,
        projets: competence.projets
          ? competence.projets.map(p => p.projet).filter(p => p !== null)
          : []
      }))

      if (processedData.length > 0) {
        cacheService.set(cacheKey, processedData)
      }

      return { data: processedData, error: null, fromCache: false }
    } catch (error) {
      console.error('Exception dans getCompetences:', error)
      return { data: [], error, fromCache: false }
    }
  },

  /**
   * Précharger toutes les données en parallèle
   */
  async preloadAll() {
    try {
      console.log('📥 Début du préchargement...')
      
      // Charger en parallèle avec gestion d'erreur individuelle
      const promises = [
        this.getAPropos().catch(err => {
          console.error('Erreur préchargement apropos:', err)
          return { data: null, error: err, fromCache: false }
        }),
        this.getFormations().catch(err => {
          console.error('Erreur préchargement formations:', err)
          return { data: [], error: err, fromCache: false }
        }),
        this.getProjets().catch(err => {
          console.error('Erreur préchargement projets:', err)
          return { data: [], error: err, fromCache: false }
        }),
        this.getCompetences().catch(err => {
          console.error('Erreur préchargement competences:', err)
          return { data: [], error: err, fromCache: false }
        })
      ]

      const results = await Promise.allSettled(promises)
      
      console.log('📊 Résultats du préchargement:', {
        apropos: results[0].status,
        formations: results[1].status,
        projets: results[2].status,
        competences: results[3].status
      })
      
      return {
        success: true,
        results: {
          apropos: results[0].status === 'fulfilled' ? results[0].value : null,
          formations: results[1].status === 'fulfilled' ? results[1].value : null,
          projets: results[2].status === 'fulfilled' ? results[2].value : null,
          competences: results[3].status === 'fulfilled' ? results[3].value : null
        }
      }
    } catch (error) {
      console.error('❌ Erreur globale lors du préchargement:', error)
      return { success: false, error }
    }
  },

  /**
   * Invalider le cache pour une ressource spécifique
   */
  invalidateCache(resource) {
    cacheService.delete(resource)
  },

  /**
   * Vider tout le cache
   */
  clearCache() {
    cacheService.clear()
  }
}
